import { useEffect, useRef } from 'react';
import { useStore } from '../store.js';

/**
 * A running account of the round.
 *
 * Toasts alone are not enough when three AI players move in a couple of
 * seconds: anything you look away from is gone. This keeps every action.
 */
export function GameLog() {
  const log = useStore((s) => s.log);
  const listRef = useRef<HTMLDivElement>(null);
  const pinnedToBottom = useRef(true);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    // Don't yank the view back down if the player has scrolled up to read.
    if (pinnedToBottom.current) list.scrollTop = list.scrollHeight;
  }, [log.length]);

  function handleScroll() {
    const list = listRef.current;
    if (!list) return;
    const distance = list.scrollHeight - list.scrollTop - list.clientHeight;
    pinnedToBottom.current = distance < 40;
  }

  return (
    <div className="log" aria-label="Game log">
      <div className="log__list" ref={listRef} onScroll={handleScroll}>
        {log.length === 0 ? (
          <p className="log__empty">Every play will be listed here as it happens.</p>
        ) : (
          log.map((entry) => (
            <p key={entry.id} className={`log__line log__line--${entry.tone}`}>
              {entry.text}
            </p>
          ))
        )}
      </div>
    </div>
  );
}

/** The most recent notable action, kept on screen until the next one. */
export function LastAction() {
  const log = useStore((s) => s.log);
  const latest = log[log.length - 1];
  if (!latest) return null;

  return (
    <p className={`last-action last-action--${latest.tone}`} aria-live="polite">
      {latest.text}
    </p>
  );
}
